import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react";
import classnames from "classnames";
import { LocalLoader } from "modules/common/components/loader/Loader";
import { Search } from "modules/common/components/form/Search";
import { DefaultSelectedOption, renderSingleOption, SimpleSelect } from "modules/common/services/form/select";
import { menuItems } from "modules/root/components/menu/MainMenu";
import { PeriodCode, periodLabel, quarterNames, quartersWithoutRate, roleLabels, YEAR_PERIOD } from "../models/calc";
import { exportEconomics, exportQuarters } from "../models/export";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { PlanrCheckbox } from "modules/common/components/planr/checkbox/PlanrCheckbox";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { UnitEconomicsStoreType, UnitSegment, UnitTab } from "../models/unit-economics-store";
import { BenchmarksDialog } from "./BenchmarksDialog";
import { EmptyState } from "./EmptyState";
import { EconomicsGrid } from "./EconomicsGrid";
import { EmployeeDrawer } from "./EmployeeDrawer";
import { QuarterMarginGrid } from "./QuarterMarginGrid";
import { BenchmarkPanel, KpiCards } from "./SummaryPanel";
import { ratio } from "./format";
import styles from "./UnitEconomicsPage.module.scss";

const menu = menuItems();

const tabs: { id: UnitTab; label: string }[] = [
    { id: "economics", label: "Unit" },
    { id: "quarters", label: "Маржа по кварталам" },
];

const periods: { id: PeriodCode; label: string }[] = [
    ...quarterNames.map((name, i) => ({ id: (i + 1) as PeriodCode, label: `${name} кв.` })),
    { id: YEAR_PERIOD, label: "Год" },
];

const segments: { id: UnitSegment; label: string }[] = [
    { id: "production", label: roleLabels.production },
    { id: "gip", label: roleLabels.gip },
    { id: "nonproduction", label: roleLabels.nonproduction },
    { id: "all", label: "Все" },
];

const ALL_DEPARTMENTS: SelectItem = { id: "", label: "Все отделы" };

export const UnitEconomicsPage = observer(({ store, setUserFilter }: UnitEconomicsPageProps) => {
    const [opened, setOpened] = useState<{ id: string; reason: boolean } | null>(null);
    const [editBenchmarks, setEditBenchmarks] = useState(false);

    useEffect(() => {
        document.title = menu.spending.nested.unit.label;
        store.load();
    }, [store]);

    const open = useCallback((id: string, reason = false) => setOpened({ id, reason }), []);
    const close = useCallback(() => setOpened(null), []);

    const data = store.data;
    const lastQuarter = data ? Math.ceil(data.lastMonth / 3) : 4;

    const yearItems = useMemo<SelectItem[]>(
        () =>
            (data?.years ?? [store.year])
                .slice()
                .reverse()
                .map((y) => ({ id: String(y), label: `${y} год` })),
        [data?.years, store.year]
    );
    const departmentItems = useMemo<SelectItem[]>(
        () => [ALL_DEPARTMENTS, ...store.departments.map((d) => ({ id: d, label: d }))],
        [store.departments]
    );

    const selectedYear = yearItems.find((y) => y.id === String(store.year)) ?? null;
    const selectedDepartment = departmentItems.find((d) => d.id === store.department) ?? ALL_DEPARTMENTS;
    const counts = store.segmentCounts;
    const rows = store.tab === "economics" ? store.rows : store.quarterRows;
    const period = store.tab === "economics" ? periodLabel(store.periodCode, store.year) : `${store.year} год`;
    const filtered =
        !!store.pureQuery ||
        !!store.department ||
        (store.tab === "economics" && (store.onlyAnomalies || store.segment !== "all"));
    const emptyText = store.pureQuery
        ? `Никого не нашлось по запросу «${store.query}»`
        : store.tab === "economics" && store.onlyAnomalies
          ? `За ${period} нет сотрудников с отклонением стоимости часа больше ${data?.deviationThreshold ?? 15}%`
          : store.department
            ? `В отделе «${store.department}» нет сотрудников этой группы за ${period}`
            : `Нет данных за ${period}`;
    const noRate = quartersWithoutRate(data, store.tab === "economics" ? store.periodCode : YEAR_PERIOD);
    const noRateNote = noRate.length
        ? `Ставка накладных за ${noRate.map((q) => quarterNames[q - 1]).join(", ")} кв. ещё не рассчитана: квартал не закрыт. Накладные за ${
              noRate.length > 1 ? "эти кварталы" : "этот квартал"
          } не начислены, маржа завышена.`
        : "";

    return (
        <div className={styles.page}>
            <LocalLoader active={store.isLoading} />

            <div className={styles.toolbar}>
                <div className={styles.segmented} role="tablist">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            role="tab"
                            aria-selected={store.tab === tab.id}
                            className={classnames(styles.segment, { [styles.segmentActive]: store.tab === tab.id })}
                            onClick={() => store.setTab(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {store.tab === "economics" && (
                    <div className={styles.periods}>
                        {periods.map((p) => {
                            const future = p.id !== YEAR_PERIOD && p.id > lastQuarter;
                            return (
                                <button
                                    key={p.id}
                                    type="button"
                                    disabled={future}
                                    title={future ? "Квартал ещё не наступил" : undefined}
                                    className={classnames(styles.period, {
                                        [styles.periodActive]: store.period === p.id,
                                    })}
                                    onClick={() => store.setPeriod(p.id)}
                                >
                                    {p.label}
                                </button>
                            );
                        })}
                    </div>
                )}

                <div className={styles.toolbarRight}>
                    <SimpleSelect
                        className={styles.yearSelect}
                        activeItem={selectedYear}
                        items={yearItems}
                        itemRenderer={renderSingleOption}
                        filterable={false}
                        onItemSelect={(item: SelectItem) => store.load(Number(item.id))}
                    >
                        <DefaultSelectedOption option={selectedYear} />
                    </SimpleSelect>
                    <PlanrButton
                        type="graybtn"
                        icon="general-download"
                        round
                        className={styles.exportButton}
                        title="Выгрузить таблицу в Excel (CSV)"
                        disabled={!rows.length}
                        onClick={() =>
                            store.tab === "economics"
                                ? exportEconomics(
                                      store.rows,
                                      `Unit, ${periodLabel(store.periodCode, store.year)}`
                                  )
                                : exportQuarters(store.quarterRows, store.year)
                        }
                    />
                    <div className={styles.searchBox}>
                        <Search
                            query={store.query}
                            onSearch={setUserFilter}
                            placeholder="Поиск сотрудника"
                            style={{ width: "100%", marginLeft: 0 }}
                        />
                    </div>
                </div>
            </div>

            {!data ? (
                <div className={styles.placeholder}>{store.isLoading ? "Собираем показатели сотрудников…" : ""}</div>
            ) : store.tab === "economics" ? (
                <>
                    {noRateNote && <div className={styles.notice}>{noRateNote}</div>}
                    {/* плавное сворачивание: высота строки сетки 1fr → 0fr */}
                    <div
                        className={classnames(styles.headerCollapse, {
                            [styles.headerCollapsed]: store.headerCollapsed,
                        })}
                        aria-hidden={store.headerCollapsed}
                    >
                        <div className={styles.headerCollapseInner}>
                            <div className={styles.headerCards}>
                                <KpiCards store={store} />
                                <BenchmarkPanel store={store} onEdit={() => setEditBenchmarks(true)} />
                            </div>
                        </div>
                    </div>
                    <div className={styles.headerToggle}>
                        <button
                            type="button"
                            className={styles.headerToggleButton}
                            onClick={store.toggleHeader}
                            aria-expanded={!store.headerCollapsed}
                            title={
                                store.headerCollapsed
                                    ? "Показать карточки показателей и загрузку"
                                    : "Свернуть карточки — в таблицу поместится больше сотрудников"
                            }
                        >
                            <GeneralIcon
                                type={store.headerCollapsed ? "general-chevron-down" : "general-chevron-up"}
                                size={12}
                            />
                            {store.headerCollapsed ? "Показатели" : "Свернуть"}
                        </button>
                    </div>

                    <div className={styles.filters}>
                        <div className={styles.chips}>
                            {segments.map((s) => (
                                <button
                                    key={s.id}
                                    type="button"
                                    className={classnames(styles.chip, { [styles.chipActive]: store.segment === s.id })}
                                    onClick={() => store.setSegment(s.id)}
                                    disabled={!counts[s.id]}
                                >
                                    {s.label}
                                    <span className={styles.chipCount}>{counts[s.id]}</span>
                                </button>
                            ))}
                        </div>
                        <div className={styles.filtersRight}>
                            <PlanrCheckbox
                                checked={store.groupByDepartment}
                                onChange={store.setGroupByDepartment}
                                disabled={!!store.department}
                                title={store.department ? "Выбран один отдел — группировать нечего" : undefined}
                                size={16}
                                className={styles.groupToggle}
                            >
                                Группировка по отделам
                            </PlanrCheckbox>
                            <div className={styles.departmentBox}>
                                <SimpleSelect
                                    className={styles.departmentSelect}
                                    activeItem={selectedDepartment}
                                    items={departmentItems}
                                    itemRenderer={renderSingleOption}
                                    filterable={false}
                                    matchTargetWidth={false}
                                    onItemSelect={(item: SelectItem) => store.setDepartment(item.id)}
                                >
                                    <DefaultSelectedOption
                                        option={selectedDepartment}
                                        onClear={store.department ? () => store.setDepartment("") : undefined}
                                    />
                                </SimpleSelect>
                            </div>
                        </div>
                    </div>

                    {rows.length > 0 && <EconomicsGrid store={store} onOpen={open} />}
                </>
            ) : (
                <>
                    <div className={styles.quartersHead}>
                        <div className={styles.quartersText}>
                            <div className={styles.quartersTitle}>Маржа производственных сотрудников, {store.year}</div>
                            <div className={styles.quartersHint}>
                                Выработка − себестоимость труда − накладные за каждый квартал. Непроизводственные
                                сотрудники не показаны: их затраты входят в накладные. Ставка накладных по кварталам:{" "}
                                {data?.rates
                                    .filter((r) => r.quarter <= lastQuarter && r.variableRate > 0)
                                    .map((r) => `${quarterNames[r.quarter - 1]} — ${ratio(r.variableRate)}`)
                                    .join(", ")}{" "}
                                ₽ на 1 ₽ труда.
                            </div>
                        </div>
                        <div className={styles.departmentBox}>
                            <SimpleSelect
                                className={styles.departmentSelect}
                                activeItem={selectedDepartment}
                                items={departmentItems}
                                itemRenderer={renderSingleOption}
                                filterable={false}
                                matchTargetWidth={false}
                                onItemSelect={(item: SelectItem) => store.setDepartment(item.id)}
                            >
                                <DefaultSelectedOption
                                    option={selectedDepartment}
                                    onClear={store.department ? () => store.setDepartment("") : undefined}
                                />
                            </SimpleSelect>
                        </div>
                    </div>
                    {noRateNote && <div className={styles.notice}>{noRateNote}</div>}
                    {rows.length > 0 && <QuarterMarginGrid store={store} onOpen={open} />}
                </>
            )}

            {!store.isLoading && data && rows.length === 0 && (
                <EmptyState
                    text={emptyText}
                    onReset={
                        filtered
                            ? () => {
                                  store.resetFilters();
                                  setUserFilter("");
                              }
                            : undefined
                    }
                />
            )}

            <EmployeeDrawer
                store={store}
                employeeId={opened?.id ?? null}
                focusReason={!!opened?.reason}
                onClose={close}
            />

            {data && (
                <BenchmarksDialog
                    isOpen={editBenchmarks}
                    benchmarks={data.benchmarks}
                    onClose={() => setEditBenchmarks(false)}
                    onSave={store.saveBenchmarks}
                />
            )}
        </div>
    );
});

interface UnitEconomicsPageProps {
    store: UnitEconomicsStoreType;
    setUserFilter: (value: string) => void;
}
